import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Box, Text, TextInput, Layer, Button,
} from 'grommet';
import { FormClose, ShareRounded } from 'grommet-icons';
import { useAppTheme } from '@Components/Context';
import {
  Seo, InfoNotice, AppButton, ShareButton,
} from '@Components/Control';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import { buildDateTimeLabel } from '@Helpers/util';
import {
  fetchCreatorReferrals,
  clearCreatorReferrals,
  fetchCreatorReferralInviteLink,
  clearCreatorReferralInviteLink,
  sendCreatorReferralInvite,
} from '@Actions';
import {
  TableWrapper,
  NoDataPlaceholder,
  tablePadding,
  renderTableHeader,
} from '../../../Partial/Product/SharedComponents';

const STATUS_LABELS = {
  pending: 'Pending',
  // eslint-disable-next-line camelcase
  signed_up: 'Signed Up',
  active: 'Active',
  // eslint-disable-next-line camelcase
  bonus_earned: 'Completed',
};

const STATUS_COLORS = {
  pending: '#f6ad55',
  // eslint-disable-next-line camelcase
  signed_up: '#3b82f6',
  active: '#22c55e',
  // eslint-disable-next-line camelcase
  bonus_earned: '#8e8e93',
};

const HEADER = [
  { display: 'Invited Email', uid: 'invitedEmail', size: '28%' },
  { display: 'Status', uid: 'status', size: '13%' },
  { display: 'Referred Company', uid: 'referredCompanyName', size: '20%' },
  { display: 'Bonus %', uid: 'bonusPercent', size: '10%' },
  {
    display: 'Months Awarded', uid: 'monthsAwarded', size: '14%', align: 'center',
  },
  { display: 'Date Invited', uid: 'createdAt', size: '15%' },
];

const CreatorReferralsPage = ({
  small,
  location,
  loading,
  submitting,
  referrals = null,
  inviteLink = null,
  fetchReferrals,
  clearReferrals,
  fetchInviteLink,
  clearInviteLink,
  sendInvite,
  paymentPending,
  referralBonusPercent = 5.0,
}) => {
  const {
    theme: { page, button },
    isDarkMode,
  } = useAppTheme();
  const usePageBg = isDarkMode ? page.background : page.altBg;
  const bonusPct = parseFloat(referralBonusPercent ?? 5.0);

  const [showModal, setShowModal] = React.useState(false);
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteSent, setInviteSent] = React.useState(false);

  React.useEffect(() => {
    fetchReferrals();
    fetchInviteLink();

    return () => {
      clearReferrals();
      clearInviteLink();
    };
  }, []);

  const handleSendInvite = () => {
    if (!inviteEmail || paymentPending) return;
    sendInvite(inviteEmail, () => {
      setInviteEmail('');
      setInviteSent(true);
      fetchReferrals();
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setInviteEmail('');
    setInviteSent(false);
  };

  const getDisplayValue = (uid, row) => {
    if (uid === 'bonusPercent' && row.bonusPercent != null) {
      const pct = Number(row.bonusPercent).toFixed(1);
      const remaining = row.monthsRemaining ?? 0;
      return `${pct}% (${remaining} mo left)`;
    }
    if (uid === 'monthsAwarded' && row.monthsAwarded != null) {
      return `${row.monthsAwarded} / ${row.awardingPeriodMonths}`;
    }
    if (uid === 'createdAt' && row.createdAt) {
      return buildDateTimeLabel(row.createdAt);
    }
    return row[uid] || '—';
  };

  const renderCell = (uid, row, display, size, align) => {
    if (uid === 'status') {
      const color = STATUS_COLORS[row.status] || page.secondaryText;
      const label = STATUS_LABELS[row.status] || row.status;
      return (
        <Box
          key={uid}
          width={small ? '100%' : size}
          pad={{
            horizontal: small ? '0' : '0.5rem',
            vertical: small ? '0.25rem' : '0',
          }}
          direction="row"
          gap="0.5rem"
        >
          {small && (
            <Text size="0.875rem" color={page.tableHeader}>
              {display}
              :
            </Text>
          )}
          <Box
            background={`${color}22`}
            border={{ color, size: '1px' }}
            round="6px"
            pad={{ vertical: '0.2rem', horizontal: '0.5rem' }}
            width="fit-content"
          >
            <Text size="0.8rem" weight={600} color={color}>
              {label}
            </Text>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        key={uid}
        width={small ? '100%' : size}
        pad={{
          horizontal: small ? '0' : '0.5rem',
          vertical: small ? '0.25rem' : '0',
        }}
        direction="row"
        justify={!small && align ? align : 'start'}
        gap="0.5rem"
      >
        {small && (
          <Text size="0.875rem" color={page.tableHeader}>
            {display}
            :
          </Text>
        )}
        <Text size="1rem" color={page.tableText}>
          {getDisplayValue(uid, row)}
        </Text>
      </Box>
    );
  };

  const renderTableContents = () => {
    if (loading || !referrals || referrals.length === 0) {
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          loading={loading}
          label="No referrals yet. Share your invite link to start earning bonuses."
          noDataLabel="No referrals yet. Share your invite link to start earning bonuses."
          textColor={page.tableText}
          loaderColor={page.highlight}
        />
      );
    }

    return (
      <Box fill="horizontal" direction="column">
        <Box
          fill="horizontal"
          wrap={false}
          direction="row"
          border={{
            color: page.tableBorder,
            size: '1px',
            style: 'solid',
            side: 'bottom',
          }}
        >
          {HEADER.map(({
            uid, display, size, align,
          }) => (
            <Box
              key={uid}
              width={size}
              pad={{ horizontal: '0.5rem', top: '1rem', bottom: '0.5rem' }}
              align={align || 'start'}
            >
              {renderTableHeader(display, small)}
            </Box>
          ))}
        </Box>
        {referrals.map((row) => (
          <Box
            key={row.id}
            direction={small ? 'column' : 'row'}
            align={small ? 'start' : 'center'}
            border={{
              color: page.tableBorder,
              size: '1px',
              style: 'solid',
              side: 'bottom',
            }}
            pad={{ vertical: '1rem', right: '0.5rem' }}
            background={page.tableBg}
            fill="horizontal"
          >
            {HEADER.map(({
              uid, display, size, align,
            }) => renderCell(uid, row, display, size, align))}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Creator Referrals',
        subTitle: 'Content ID',
      }}
    >
      <Seo />
      <Box flex background={usePageBg} pad={{ vertical: '1.5rem' }} gap="1rem">
        <InfoNotice
          header={`Earn ${bonusPct}% from every referral`}
          text={`Invite creators to join Redflag and receive ${bonusPct}% of the revenue generated from their content after they sign up.`}
        />

        <TableWrapper
          small={small}
          header="Your referrals"
          boxPad={tablePadding(small)}
          totalResults={referrals?.length || 0}
          currentResults={referrals?.length || 0}
          tableControls={(
            <AppButton
              overrideHover
              disabled={loading || paymentPending}
              onClick={() => setShowModal(true)}
              level="tableControls"
              color={button.primaryText}
              bgColor={button.primaryBg}
              label="+ Invite creators"
            />
          )}
        >
          <Box fill="horizontal" direction="column">
            {renderTableContents()}
          </Box>
        </TableWrapper>
      </Box>

      {showModal && (
        <Layer
          onEsc={handleCloseModal}
          onClickOutside={handleCloseModal}
          responsive={false}
          animate={false}
        >
          <Box
            pad="1.5rem"
            width={small ? '90vw' : '40rem'}
            background={page.modalBg}
            gap="1.5rem"
            round="0.5rem"
          >
            <Box direction="row" justify="between" align="center">
              <Text size="1.25rem" weight={600} color={page.modalText}>
                Invite a creator
              </Text>
              <Button
                plain
                icon={<FormClose />}
                onClick={handleCloseModal}
                style={{ padding: '0.5rem' }}
              />
            </Box>

            <Box gap="0.75rem">
              <Text size="0.875rem" weight={600} color={page.modalText}>
                Your referral link
              </Text>
              {inviteLink ? (
                <Box
                  direction={small ? 'column' : 'row'}
                  gap="0.75rem"
                  align={small ? 'start' : 'center'}
                >
                  <Box
                    border={{ color: page.inputBorder, size: '1px' }}
                    round="var(--app-radius)"
                    pad={{ horizontal: '0.75rem', vertical: '0.5rem' }}
                    flex
                    background={usePageBg}
                  >
                    <Text size="0.85rem" color={page.secondaryText} truncate>
                      {inviteLink.inviteUrl}
                    </Text>
                  </Box>
                  <ShareButton
                    small={small}
                    textColor={button.primaryText}
                    buttonBg={button.primaryBg}
                    buttonLabel="Copy link"
                    renderKey="creator-referral-link-modal"
                    copyMessage="Referral link copied!"
                    copyValue={inviteLink.inviteUrl}
                    icon={
                      <ShareRounded color={button.primaryText} size="1rem" />
                    }
                  />
                </Box>
              ) : (
                <Text size="0.875rem" color={page.secondaryText}>
                  Loading your referral link...
                </Text>
              )}
            </Box>

            <Box gap="0.75rem">
              <Text size="0.875rem" weight={600} color={page.modalText}>
                Send an invite by email
              </Text>
              <Text size="0.875rem" color={page.modalText}>
                Enter the email address of a creator you want to invite. They
                will receive an onboarding link with your referral code.
              </Text>
              {inviteSent && (
                <Text size="0.875rem" color="var(--app-success, #22c55e)">
                  Invite sent successfully!
                </Text>
              )}
              <TextInput
                placeholder="creator@example.com"
                value={inviteEmail}
                onChange={(e) => {
                  setInviteEmail(e.target.value);
                  setInviteSent(false);
                }}
                type="email"
              />
            </Box>

            <Box direction="row" gap="1rem" justify="end">
              <AppButton
                overrideHover
                disabled={submitting}
                onClick={handleCloseModal}
                level="tableControls"
                color={button.primaryText}
                bgColor={button.primaryBg}
                label="Cancel"
              />
              <AppButton
                overrideHover
                disabled={!inviteEmail || submitting || paymentPending}
                onClick={handleSendInvite}
                level="tableControls"
                color={button.primaryText}
                bgColor={button.primaryBg}
                label={submitting ? 'Sending...' : 'Send invite'}
              />
            </Box>
          </Box>
        </Layer>
      )}
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    loading: state.fetchLoader.dataLoading,
    submitting: state.fetchLoader.submissionLoading,
    referrals: state.creatorReferral.referrals,
    inviteLink: state.creatorReferral.inviteLink,
    paymentPending: state.auth.paymentPending,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchReferrals: fetchCreatorReferrals,
      clearReferrals: clearCreatorReferrals,
      fetchInviteLink: fetchCreatorReferralInviteLink,
      clearInviteLink: clearCreatorReferralInviteLink,
      sendInvite: sendCreatorReferralInvite,
    },
    dispatch,
  );
}

CreatorReferralsPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  referrals: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      invitedEmail: PropTypes.string,
      status: PropTypes.string.isRequired,
      referredCompanyName: PropTypes.string,
      bonusPercent: PropTypes.number,
      awardingPeriodMonths: PropTypes.number,
      monthsAwarded: PropTypes.number,
      monthsRemaining: PropTypes.number,
      createdAt: PropTypes.string,
    }),
  ),
  inviteLink: PropTypes.shape({
    token: PropTypes.string.isRequired,
    inviteUrl: PropTypes.string.isRequired,
  }),
  fetchReferrals: PropTypes.func.isRequired,
  clearReferrals: PropTypes.func.isRequired,
  fetchInviteLink: PropTypes.func.isRequired,
  clearInviteLink: PropTypes.func.isRequired,
  sendInvite: PropTypes.func.isRequired,
  paymentPending: PropTypes.bool.isRequired,
  referralBonusPercent: PropTypes.number,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withProductAuth(CreatorReferralsPage));
